import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import MobileNav from '@/components/MobileNav';
import { ChevronLeft, ChevronRight, Clock, Zap, BookOpen, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LessonViewer() {
  const navigate = useNavigate();
  const { moduleId, lessonId } = useParams();
  const { token } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [lessonCompleted, setLessonCompleted] = useState(false);
  const [xpEarned, setXpEarned] = useState(0);

  useEffect(() => {
    fetchLesson();
  }, [moduleId, lessonId]);

  const fetchLesson = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [lessonRes, moduleRes, progressRes] = await Promise.all([
        axios.get(`${API}/education/modules/${moduleId}/lessons/${lessonId}`, { headers }),
        axios.get(`${API}/education/modules/${moduleId}`, { headers }),
        axios.get(`${API}/education/progress`, { headers }),
      ]);
      setLesson(lessonRes.data);
      setModule(moduleRes.data);
      setLessonCompleted(progressRes.data?.completed_lessons?.includes(lessonId) || false);
    } catch (err) {
      console.error('Failed to fetch lesson:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (idx) => {
    if (showResult) return;
    setSelectedAnswer(idx);
  };

  const handleAnswerSubmit = () => {
    if (selectedAnswer === null) return;
    const correct = lesson.quiz[currentQuestion].correct === selectedAnswer;
    if (correct) setScore(s => s + 1);
    setShowResult(true);
  };

  const handleNextQuestion = () => {
    setShowResult(false);
    setSelectedAnswer(null);
    if (currentQuestion + 1 < lesson.quiz.length) {
      setCurrentQuestion(q => q + 1);
    } else {
      setQuizCompleted(true);
      completeLesson();
    }
  };

  const completeLesson = async () => {
    try {
      const res = await axios.post(
        `${API}/education/modules/${moduleId}/lessons/${lessonId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setXpEarned(res.data.xp_earned || 0);
      setLessonCompleted(true);
      toast.success(`Leçon complétée ! +${res.data.xp_earned || 0} XP`);
    } catch (err) {
      console.error('Failed to mark lesson complete:', err);
    }
  };

  const getLessonIndex = () => {
    if (!module?.lessons) return 0;
    return module.lessons.findIndex(l => l.id === lessonId);
  };

  const getNextLesson = () => {
    if (!module?.lessons) return null;
    const idx = getLessonIndex();
    return module.lessons[idx + 1] || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#ff6b35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center gap-4">
        <p className="text-gray-400">Leçon introuvable</p>
        <Button onClick={() => navigate('/academie')} variant="outline">Retour à l'Académie</Button>
      </div>
    );
  }

  const lessonIdx = getLessonIndex();
  const nextLesson = getNextLesson();

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <MobileNav currentPage="academie" />

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <button onClick={() => navigate('/academie')} className="hover:text-[#ff6b35] transition-colors">Académie</button>
          <ChevronRight className="w-4 h-4" />
          <button onClick={() => navigate(`/academie/module/${moduleId}`)} className="hover:text-[#ff6b35] transition-colors">{module?.title}</button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">{lesson.title}</span>
        </div>

        {/* Lesson Header */}
        <div className="glass rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                <BookOpen className="w-4 h-4" />
                <span>Leçon {lessonIdx + 1} sur {module?.lessons?.length}</span>
                <span>•</span>
                <Clock className="w-4 h-4" />
                <span>{lesson.duration_minutes} min</span>
                <span>•</span>
                <Zap className="w-4 h-4 text-[#ff6b35]" />
                <span className="text-[#ff6b35]">+{lesson.xp_reward} XP</span>
              </div>
              <h1 className="text-2xl font-bold text-white">{lesson.title}</h1>
            </div>
            {lessonCompleted && (
              <div className="flex items-center gap-2 text-green-400 bg-green-400/10 px-4 py-2 rounded-full">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-semibold">Complétée</span>
              </div>
            )}
          </div>
        </div>

        {/* Lesson Content or Quiz */}
        {!quizStarted ? (
          <>
            {/* Lesson Content */}
            <div className="glass rounded-2xl p-8 mb-6 prose prose-invert prose-orange max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({children}) => <h1 className="text-2xl font-bold text-white mb-4">{children}</h1>,
                  h2: ({children}) => <h2 className="text-xl font-bold text-[#ff6b35] mt-6 mb-3">{children}</h2>,
                  h3: ({children}) => <h3 className="text-lg font-semibold text-gray-200 mt-4 mb-2">{children}</h3>,
                  p: ({children}) => <p className="text-gray-300 leading-relaxed mb-4">{children}</p>,
                  strong: ({children}) => <strong className="text-white font-bold">{children}</strong>,
                  ul: ({children}) => <ul className="list-disc list-inside text-gray-300 space-y-1 mb-4">{children}</ul>,
                  ol: ({children}) => <ol className="list-decimal list-inside text-gray-300 space-y-1 mb-4">{children}</ol>,
                  li: ({children}) => <li className="text-gray-300">{children}</li>,
                  table: ({children}) => (
                    <div className="overflow-x-auto mb-4">
                      <table className="w-full border-collapse">{children}</table>
                    </div>
                  ),
                  thead: ({children}) => <thead className="bg-[#ff6b35]/10">{children}</thead>,
                  th: ({children}) => <th className="text-left p-3 text-[#ff6b35] font-semibold border-b border-[#ff6b35]/20">{children}</th>,
                  td: ({children}) => <td className="p-3 text-gray-300 border-b border-gray-800">{children}</td>,
                  code: ({children}) => <code className="bg-gray-800 text-[#ff6b35] px-2 py-1 rounded text-sm font-mono">{children}</code>,
                  pre: ({children}) => <pre className="bg-gray-900 border border-gray-700 rounded-xl p-4 overflow-x-auto mb-4">{children}</pre>,
                  blockquote: ({children}) => <blockquote className="border-l-4 border-[#ff6b35] pl-4 text-gray-400 italic my-4">{children}</blockquote>,
                }}
              >
                {lesson.content}
              </ReactMarkdown>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between gap-4">
              <Button
                onClick={() => navigate(`/academie/module/${moduleId}`)}
                variant="outline"
                className="border-gray-700 text-gray-400 hover:text-white"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Retour au module
              </Button>

              {lesson.quiz && lesson.quiz.length > 0 ? (
                <Button
                  data-testid="start-quiz-btn"
                  onClick={() => setQuizStarted(true)}
                  className="bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-bold px-8"
                >
                  Passer le Quiz →
                </Button>
              ) : (
                <Button
                  data-testid="complete-lesson-btn"
                  onClick={completeLesson}
                  disabled={lessonCompleted}
                  className="bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-bold px-8"
                >
                  {lessonCompleted ? 'Déjà complétée ✓' : 'Marquer comme lu'}
                </Button>
              )}
            </div>
          </>
        ) : quizCompleted ? (
          /* Quiz Results */
          <div className="glass rounded-2xl p-8 text-center">
            <div className="text-6xl mb-4">
              {score === lesson.quiz.length ? '🏆' : score >= lesson.quiz.length / 2 ? '🎯' : '📚'}
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {score === lesson.quiz.length ? 'Parfait !' : score >= lesson.quiz.length / 2 ? 'Bien joué !' : 'Continuez à apprendre !'}
            </h2>
            <p className="text-gray-400 mb-2">Score : <span className="text-[#ff6b35] font-bold text-2xl">{score}/{lesson.quiz.length}</span></p>
            {xpEarned > 0 && (
              <div className="inline-flex items-center gap-2 bg-[#ff6b35]/10 border border-[#ff6b35]/30 rounded-full px-4 py-2 mb-6">
                <Zap className="w-4 h-4 text-[#ff6b35]" />
                <span className="text-[#ff6b35] font-bold">+{xpEarned} XP gagnés !</span>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
              <Button
                onClick={() => navigate(`/academie/module/${moduleId}`)}
                variant="outline"
                className="border-gray-700 text-gray-300"
              >
                Retour au Module
              </Button>
              {nextLesson && (
                <Button
                  data-testid="next-lesson-btn"
                  onClick={() => navigate(`/academie/module/${moduleId}/lesson/${nextLesson.id}`)}
                  className="bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-bold"
                >
                  Leçon Suivante →
                </Button>
              )}
            </div>
          </div>
        ) : (
          /* Quiz Interface */
          <div className="glass rounded-2xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Quiz</h2>
              <span className="text-gray-400 text-sm">Question {currentQuestion + 1}/{lesson.quiz.length}</span>
            </div>

            <div className="w-full bg-gray-800 rounded-full h-2 mb-8">
              <div
                className="bg-[#ff6b35] h-2 rounded-full transition-all"
                style={{ width: `${((currentQuestion) / lesson.quiz.length) * 100}%` }}
              />
            </div>

            <p className="text-lg font-semibold text-white mb-6">
              {lesson.quiz[currentQuestion].question}
            </p>

            <div className="space-y-3 mb-6">
              {lesson.quiz[currentQuestion].options.map((option, idx) => {
                let btnClass = 'w-full text-left p-4 rounded-xl border transition-all ';
                if (!showResult) {
                  btnClass += selectedAnswer === idx
                    ? 'border-[#ff6b35] bg-[#ff6b35]/10 text-white'
                    : 'border-gray-700 text-gray-300 hover:border-[#ff6b35]/50 hover:bg-gray-800';
                } else {
                  if (idx === lesson.quiz[currentQuestion].correct) {
                    btnClass += 'border-green-400 bg-green-400/10 text-green-400';
                  } else if (idx === selectedAnswer && idx !== lesson.quiz[currentQuestion].correct) {
                    btnClass += 'border-red-400 bg-red-400/10 text-red-400';
                  } else {
                    btnClass += 'border-gray-700 text-gray-500';
                  }
                }
                return (
                  <button
                    key={idx}
                    data-testid={`quiz-option-${idx}`}
                    className={btnClass}
                    onClick={() => handleAnswerSelect(idx)}
                    disabled={showResult}
                  >
                    <span className="font-semibold mr-3 text-gray-500">{String.fromCharCode(65 + idx)}.</span>
                    {option}
                  </button>
                );
              })}
            </div>

            {showResult && (
              <div className={`p-4 rounded-xl mb-6 ${selectedAnswer === lesson.quiz[currentQuestion].correct ? 'bg-green-400/10 border border-green-400/30' : 'bg-red-400/10 border border-red-400/30'}`}>
                <p className={`font-semibold mb-1 ${selectedAnswer === lesson.quiz[currentQuestion].correct ? 'text-green-400' : 'text-red-400'}`}>
                  {selectedAnswer === lesson.quiz[currentQuestion].correct ? '✓ Bonne réponse !' : '✗ Mauvaise réponse'}
                </p>
                <p className="text-gray-300 text-sm">{lesson.quiz[currentQuestion].explanation}</p>
              </div>
            )}

            <div className="flex justify-end">
              {!showResult ? (
                <Button
                  data-testid="submit-answer-btn"
                  onClick={handleAnswerSubmit}
                  disabled={selectedAnswer === null}
                  className="bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-bold px-8"
                >
                  Valider
                </Button>
              ) : (
                <Button
                  data-testid="next-question-btn"
                  onClick={handleNextQuestion}
                  className="bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-bold px-8"
                >
                  {currentQuestion + 1 < lesson.quiz.length ? 'Question Suivante →' : 'Voir les Résultats →'}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
